import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import Renderable from '../../../Component/Renderable';
import { Easing, Tween } from '@tweenjs/tween.js';
import { AwaitIterators, ConcatIterators } from '../AnimationHelper';
import { delay } from '../../../Utility/Task/TaskQueue';
import { tVector4 } from '../../../Utility/ArrayHelper';
import ColorFilter from '../../../Component/ColorFilter';
import TransformComponent from 'Component/Transform';

interface IQuestItemCompleteIntroMeta {
    initialDelay: number;
    button: GameObject;
    borderGlow: GameObject;
    buttonShine: GameObject;
    rewardShine: GameObject;
    backgroundCompleted: GameObject;
    backgroundActive: GameObject;
    coinSpin?: GameObject;
}
export class QuestItemCompleteIntro extends AnimationClip<
    IQuestItemCompleteIntroMeta
> {
    public *execute(
        gameObject: GameObject,
        meta?: IQuestItemCompleteIntroMeta
    ): IterableIterator<number> {
        const rewardShineRenderable: Renderable = meta.rewardShine.getComponent(
            Renderable
        );
        const backgroundCompletedRenderable: Renderable = meta.backgroundCompleted.getComponent(
            Renderable
        );
        const backgroundActiveRenderable: Renderable = meta.backgroundActive.getComponent(
            Renderable
        );
        const borderGlowRenderable: Renderable = meta.borderGlow.getComponent(
            Renderable
        );
        const buttonTransform: TransformComponent = meta.button.getComponent(
            TransformComponent
        );
        const buttonRenderable: Renderable = meta.button.getComponent(
            Renderable
        );
        const completedColorFilter = meta.backgroundCompleted.addComponent(
            ColorFilter
        );

        backgroundCompletedRenderable.alpha = 0;
        backgroundCompletedRenderable.visible = true;
        borderGlowRenderable.alpha = 0;
        borderGlowRenderable.visible = true;
        buttonRenderable.alpha = 0;

        const brightnessTweens: Tween[] = [
            this.getBrightnessTween(completedColorFilter, 2, 250),
            this.getBrightnessTween(completedColorFilter, 1, 200),
        ];
        yield* delay(meta.initialDelay);
        yield* AwaitIterators([
            // Start the fade in/out tweens
            this.startTween(this.getAlphaTween(backgroundActiveRenderable, 0)),
            this.startTween(
                this.getAlphaTween(backgroundCompletedRenderable, 1)
            ),
            this.startTween(this.getAlphaTween(borderGlowRenderable, 1)),
            // Also start the brightness tweens
            ConcatIterators([
                ...brightnessTweens.map(tween => this.startTween(tween)),
                // Wait for brightness tweens to finish before sliding the button in and fading in the reward shine
                AwaitIterators([
                    this.startTween(
                        this.getAlphaTween(buttonRenderable, 1, 350)
                    ),
                    this.startTween(
                        this.getPositionYTween(buttonTransform, 70, 350)
                    ),
                    this.startTween(
                        this.getAlphaTween(rewardShineRenderable, 1, 450)
                    ),
                ]),
            ]),
        ]);
        meta.backgroundCompleted.removeComponent(ColorFilter);
    }

    private getAlphaTween(
        renderable: Renderable,
        alpha: number,
        duration: number = 250,
        easing: (k: number) => number = Easing.Exponential.InOut
    ): Tween {
        return this.getTween(renderable)
            .to({ alpha }, duration)
            .easing(easing);
    }
    private getPositionYTween(
        transform: TransformComponent,
        y: number,
        duration: number = 250,
        easing: (k: number) => number = Easing.Quadratic.Out
    ): Tween {
        return this.getTween(transform)
            .to({ y }, duration)
            .easing(easing);
    }
    private getBrightnessTween(
        colorFilter: ColorFilter,
        brightness: number,
        duration: number = 250,
        easing: (k: number) => number = Easing.Quadratic.Out
    ): Tween {
        return this.getTween(colorFilter)
            .to(
                {
                    brightness,
                },
                duration
            )
            .easing(easing);
    }
}
